import React, { SVGProps, useEffect, useState } from "react";
import { Button, cn } from "@adora/shadcn-ui";
import Auth, { SocialLoginProvider } from "../../utils/auth";
import GoogleLogo from "./googleLogo.svg";
import MicrosoftLogo from "./microsoftLogo.svg";

type LogoComponent = React.FC<SVGProps<SVGSVGElement>>;

const PROVIDERS: { provider: SocialLoginProvider; name: string; label: string; Logo: LogoComponent }[] = [
  { provider: "google", name: "Google", label: "Continue with Google", Logo: GoogleLogo as unknown as LogoComponent },
  {
    provider: "microsoft",
    name: "Microsoft",
    label: "Continue with Microsoft",
    Logo: MicrosoftLogo as unknown as LogoComponent
  }
];

export interface SocialLoginButtonsProps {
  /** Where to land after sign-in, carried across the provider round trip. */
  redirectUrl?: string | null;
  className?: string;
}

const SocialLoginButtons: React.FC<SocialLoginButtonsProps> = ({ redirectUrl, className }) => {
  const [pendingProvider, setPendingProvider] = useState<SocialLoginProvider | null>(null);
  const [error, setError] = useState("");
  const [unfinishedProvider, setUnfinishedProvider] = useState<SocialLoginProvider | null>(null);

  // Landing here with a sign-in still in flight means the provider never sent the
  // user back, e.g. their company blocked Adora on the provider's own error page.
  useEffect(() => {
    const noticeUnfinished = () => {
      const provider = Auth.takeUnfinishedSocialLogin();
      if (provider) {
        setUnfinishedProvider(provider);
      }
    };
    noticeUnfinished();

    // Coming back from the provider can restore this page from the back/forward
    // cache with the buttons still pending, so re-enable them.
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setPendingProvider(null);
        noticeUnfinished();
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const handleClick = async (provider: SocialLoginProvider) => {
    setPendingProvider(provider);
    setError("");
    setUnfinishedProvider(null);
    const result = await Auth.startSocialLogin(provider, redirectUrl);
    result.match(
      () => {
        setError("Couldn't start sign-in. Please try again.");
        setPendingProvider(null);
      },
      (authorizationUrl) => {
        // eslint-disable-next-line no-restricted-syntax -- leaving the app for the provider's sign-in page
        window.location.assign(authorizationUrl);
      }
    );
  };

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {PROVIDERS.map(({ provider, label, Logo }) => (
        <Button
          key={provider}
          type="button"
          variant="outline"
          size="lg"
          data-action-name={`login ${provider} button`}
          disabled={pendingProvider !== null}
          onClick={() => handleClick(provider)}
          className="h-12 w-full gap-3 rounded-full border border-border bg-background font-poppins text-foreground"
        >
          <Logo width="18" height="18" aria-hidden="true" />
          {pendingProvider === provider ? "Redirecting..." : label}
        </Button>
      ))}
      {error ? <p className="font-poppins text-body2 text-destructive">{error}</p> : null}
      {unfinishedProvider ? (
        <p role="status" className="font-poppins text-body2 text-muted-foreground">
          {PROVIDERS.find(({ provider }) => provider === unfinishedProvider)?.name} sign-in didn&apos;t complete. Please
          sign in with your username and password instead.
        </p>
      ) : null}
    </div>
  );
};

export default SocialLoginButtons;
